import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ANONYMOUS,
  actorOf,
  can,
  canAny,
  type Actor,
  type Capability,
  type CapabilityStore,
} from '@lemontaigu/platform';

/**
 * The capability context — one fetch per session, every hide/unhide decision
 * answered locally from it (ADR-0013, ladder step 8).
 *
 * WHAT IT REPLACES. Four SPAs each fired `supabase.rpc('is_admin')` from inside
 * a view's `useEffect` (Planning, Pointages, Synthèses, …): an authorization
 * question asked over the network, per component, per mount, answering exactly
 * one coarse boolean. `my_capabilities()` returns the whole effective set in
 * one round trip, so a new affordance costs a `useCapability(...)` call rather
 * than another probe — and the answer is the SAME data RLS enforces with, so
 * the button and the policy cannot disagree.
 *
 * ⚠ HIDING IS COSMETIC. Everything here decides what to RENDER. It stops an
 * operator being shown a control that would fail, which is a usability
 * property, not a security one — the browser holds a real Postgres session, so
 * the policy is the boundary (ARCHITECTURE.md §6, ADR-0013 §7). Never use
 * `useCapability` as the only thing standing between a user and an action.
 *
 * FAILS CLOSED. While loading, and after a failed fetch, the actor has NO
 * capabilities: controls stay hidden. The alternative — showing everything
 * until proven otherwise — flashes affordances the server will refuse.
 */

interface CapabilityContextValue {
  actor: Actor;
  /** False once the first fetch has settled, successfully or not. */
  loading: boolean;
  /** Non-null if the fetch failed. The set is empty in that case. */
  error: string | null;
  /** Re-fetch — after entering an access mode, or after a grant changes. */
  refresh: () => Promise<void>;
}

const CapabilityContext = createContext<CapabilityContextValue | null>(null);

export interface CapabilityProviderProps {
  /** Supplied by the app's composition root. Never imported here — this file
   *  names no infrastructure, same contract as the auth gates. */
  store: CapabilityStore;
  children: ReactNode;
}

export function CapabilityProvider({ store, children }: CapabilityProviderProps) {
  const [actor, setActor] = useState<Actor>(ANONYMOUS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const value = useMemo<CapabilityContextValue>(() => {
    const load = async () => {
      setLoading(true);
      try {
        setActor(actorOf(await store.mine()));
        setError(null);
      } catch (e) {
        // Fail closed: drop every capability, surface the reason.
        setActor(ANONYMOUS);
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    };
    return { actor, loading, error, refresh: load };
  }, [actor, loading, error, store]);

  useEffect(() => {
    void value.refresh();
    // Deliberately keyed on `store` alone: the composition root supplies a
    // stable instance, so this fetches once per session rather than on every
    // state change (which `value` in the deps would cause).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  return <CapabilityContext.Provider value={value}>{children}</CapabilityContext.Provider>;
}

function useCapabilityContext(): CapabilityContextValue {
  const ctx = useContext(CapabilityContext);
  if (!ctx) {
    throw new Error(
      'useCapability must be used inside a <CapabilityProvider>. Mount it in the app composition root.',
    );
  }
  return ctx;
}

/** The whole context — for a screen that needs `loading`/`error`/`refresh`. */
export function useCapabilities(): CapabilityContextValue {
  return useCapabilityContext();
}

/** Does the current actor hold this capability? False while loading. */
export function useCapability(cap: Capability): boolean {
  return can(useCapabilityContext().actor, cap);
}

/** Any-of. Mirrors SQL `has_capability(text[])`. */
export function useAnyCapability(caps: readonly Capability[]): boolean {
  return canAny(useCapabilityContext().actor, caps);
}

export interface CanProps {
  capability: Capability | readonly Capability[];
  children: ReactNode;
  /** Rendered instead when the actor lacks it. Default: nothing. */
  fallback?: ReactNode;
}

/**
 * Declarative hide/unhide.
 *
 *   <Can capability={CAPABILITIES.RH_BOARD_ASSIGN}>
 *     <AssignButton />
 *   </Can>
 *
 * An array means any-of.
 */
export function Can({ capability, children, fallback = null }: CanProps) {
  const { actor } = useCapabilityContext();
  const allowed = Array.isArray(capability)
    ? canAny(actor, capability)
    : can(actor, capability as Capability);
  return <>{allowed ? children : fallback}</>;
}
